import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, BookOpen, Wrench, LifeBuoy, Cog, FileSpreadsheet, Download, ExternalLink, X, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  'instruction-manual': BookOpen,
  'maintenance-guide': Wrench,
  'troubleshooting-guide': LifeBuoy,
  'tools-software': Cog,
  'spec-sheet': FileSpreadsheet,
  other: FileText,
};

const LABELS = {
  'instruction-manual': 'Instruction Manual',
  'maintenance-guide': 'Maintenance Guide',
  'troubleshooting-guide': 'Troubleshooting Guide',
  'tools-software': 'Tools & Software',
  'spec-sheet': 'Spec Sheet',
  other: 'Document',
};

export const FeaturedDownloads = () => {
  const [docs, setDocs] = useState([]);
  const [gateDoc, setGateDoc] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/documents/featured`)
      .then((res) => setDocs(res.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  if (docs.length === 0) return null;

  const urlFor = (doc) => (doc.external_url ? doc.external_url : (doc.file_url ? `${API_URL}${doc.file_url}` : '#'));

  const submitGate = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email to get your download.');
      return;
    }
    const url = urlFor(gateDoc);
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: gateDoc.title,
        download_url: gateDoc.external_url ? url : `${window.location.origin.replace(/\/$/, '')}${gateDoc.file_url}`,
        category: gateDoc.category,
      });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    window.open(url, '_blank', 'noopener, noreferrer');
    toast.success('Sent to your inbox, opening now too');
    setGateDoc(null);
  };

  return (
    <section className="py-20 section-padding bg-white" data-testid="featured-downloads">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Popular downloads</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Manuals &amp; guides, ready to grab</h2>
            <p className="text-slate-600 mt-3">The instruction manuals and maintenance guides customers reach for most, free to download.</p>
          </div>
          <Link to="/documents" className="inline-flex items-center gap-2 text-[#003456] font-bold hover:text-[#CE8946] transition-colors whitespace-nowrap" data-testid="featured-view-all">
            View all downloads <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {docs.map((doc) => {
            const Icon = ICONS[doc.category] || FileText;
            const isExternal = !!doc.external_url;
            return (
              <div key={doc.id} className="bg-[#F1F5F9] rounded-2xl p-6 border border-slate-200 hover:border-[#CE8946] hover:shadow-lg transition-all flex flex-col" data-testid={`featured-card-${doc.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#003456] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="h-6 w-6 text-[#CE8946]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#003456] leading-snug">{doc.title}</h3>
                    <p className="text-slate-500 text-sm mt-1 line-clamp-2">{doc.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <span className="text-xs px-2.5 py-1 bg-white rounded-full text-slate-600">{LABELS[doc.category] || 'Document'}</span>
                  {doc.file_type && <span className="text-xs px-2.5 py-1 bg-white rounded-full text-slate-600 uppercase">{doc.file_type}</span>}
                </div>
                <button
                  onClick={() => { setGateDoc(doc); setEmail(''); }}
                  className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-center gap-2 w-full text-[#003456] font-bold text-sm hover:text-[#CE8946] transition-colors"
                  data-testid={`featured-download-${doc.id}`}
                >
                  {isExternal ? <ExternalLink className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                  {isExternal ? 'Open link' : 'Download'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {gateDoc && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="featured-gate-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
            <button onClick={() => setGateDoc(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="featured-gate-close">
              <X className="h-5 w-5" />
            </button>
            <div className="w-12 h-12 bg-[#003456] rounded-xl flex items-center justify-center mb-4">
              <Download className="h-6 w-6 text-[#CE8946]" />
            </div>
            <h3 className="text-xl font-bold text-[#003456]">Get &ldquo;{gateDoc.title}&rdquo;</h3>
            <p className="text-slate-600 text-sm mt-2">Enter your email and we'll send your copy, plus open it right away.</p>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitGate(); }}
              placeholder="you@email.com"
              className="mt-5 w-full px-4 py-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
              data-testid="featured-gate-email"
            />
            <button
              onClick={submitGate}
              disabled={submitting}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] disabled:opacity-60 text-[#003456] font-bold px-6 py-3 rounded-md transition-colors"
              data-testid="featured-gate-submit"
            >
              {submitting ? 'Sending...' : (<><CheckCircle2 className="h-4 w-4" /> Get my download</>)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default FeaturedDownloads;
